import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Camera, X, MapPin, Calendar, Phone, EnvelopeSimple, Storefront, Plus, Trash, Gift, VideoCamera } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_CATEGORIES = ['Furniture', 'Antiques', 'Electronics', 'Clothing', 'Books', 'Tools', 'Kitchenware', 'Art', 'Jewelry', 'Toys', 'Sports', 'Other'];

export default function CreateSale() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: '', description: '', category: 'estate',
    address: '', city: '', state: '', zip_code: '',
    start_date: '', end_date: '',
    contact_name: user?.name || '', contact_phone: '', contact_email: user?.email || '',
    items_categories: [],
  });
  const [photos, setPhotos] = useState([]); // { file, preview, uploading, path }
  const [videos, setVideos] = useState([]); // { file, name, uploading, path }
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const isPremium = user?.subscription === 'premium';
  const freeListings = user?.free_listings || 0;
  const hasFreeCredit = freeListings > 0;
  const maxFreePhotos = 5;
  const extraPhotoPackCost = 2;
  const photoPackSize = 5;

  const VENDOR_CATEGORIES = ['craft_fair', 'food_vendor', 'carnival', 'vendor_event'];
  const isVendorEvent = VENDOR_CATEGORIES.includes(form.category);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
      path: null,
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[idx].preview);
      updated.splice(idx, 1);
      return updated;
    });
  };

  const toggleItemCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      items_categories: prev.items_categories.includes(cat)
        ? prev.items_categories.filter(c => c !== cat)
        : [...prev.items_categories, cat],
    }));
  };

  const calculateCost = () => {
    if (isPremium || hasFreeCredit) return { listing: 0, photos: 0, videos: 0, total: 0 };
    if (isVendorEvent) {
      const vendorCost = 25;
      const videoCost = videos.length * 2;
      return { listing: vendorCost, photos: 0, videos: videoCost, total: vendorCost + videoCost };
    }
    const listingCost = 3;
    const photoCost = photos.length > maxFreePhotos
      ? Math.ceil((photos.length - maxFreePhotos) / photoPackSize) * extraPhotoPackCost
      : 0;
    return { listing: listingCost, photos: photoCost, videos: 0, total: listingCost + photoCost };
  };

  const uploadPhotos = async () => {
    const uploaded = [];
    for (let i = 0; i < photos.length; i++) {
      if (photos[i].path) { uploaded.push(photos[i].path); continue; }
      const formData = new FormData();
      formData.append('file', photos[i].file);
      try {
        const { data } = await axios.post(`${API}/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(data.path);
        setPhotos(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], path: data.path, uploading: false };
          return updated;
        });
      } catch (err) {
        toast.error(`Failed to upload ${photos[i].file.name}`);
      }
    }
    return uploaded;
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    const newVids = files.map(file => ({
      file, name: file.name, uploading: false, path: null,
    }));
    setVideos(prev => [...prev, ...newVids]);
  };

  const removeVideo = (idx) => {
    setVideos(prev => { const u = [...prev]; u.splice(idx, 1); return u; });
  };

  const uploadVideos = async () => {
    const uploaded = [];
    for (let i = 0; i < videos.length; i++) {
      if (videos[i].path) { uploaded.push(videos[i].path); continue; }
      const formData = new FormData();
      formData.append('file', videos[i].file);
      try {
        const { data } = await axios.post(`${API}/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(data.path);
      } catch (err) {
        toast.error(`Failed to upload ${videos[i].name}`);
      }
    }
    return uploaded;
  };

  const geocodeAddress = async () => {
    const fullAddress = `${form.address}, ${form.city}, ${form.state} ${form.zip_code}`;
    setGeocoding(true);
    try {
      const { data } = await axios.get(`${API}/geocode?address=${encodeURIComponent(fullAddress)}`);
      return { lat: data.lat, lng: data.lng };
    } catch {
      return { lat: null, lng: null };
    } finally {
      setGeocoding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.address || !form.city || !form.state || !form.zip_code || !form.start_date || !form.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      // Upload photos
      const photoPaths = await uploadPhotos();

      // Upload videos
      const videoPaths = await uploadVideos();

      // Geocode address
      const { lat, lng } = await geocodeAddress();

      // Create sale
      const { data: sale } = await axios.post(`${API}/sales`, {
        ...form, photo_paths: photoPaths, video_paths: videoPaths, lat, lng,
      }, { withCredentials: true });

      // If premium, admin, or has free listing credit, sale is active immediately
      if (isPremium || user?.role === 'admin' || hasFreeCredit) {
        toast.success(hasFreeCredit && !isPremium && user?.role !== 'admin'
          ? 'Sale posted using your free listing credit!'
          : 'Sale posted successfully!');
        navigate(`/sale/${sale.id}`);
      } else {
        // Need to pay - create checkout
        const packageType = isVendorEvent ? 'vendor_listing' : 'listing';

        const { data: checkout } = await axios.post(`${API}/payments/create-checkout`, {
          package_type: packageType,
          origin_url: window.location.origin,
          sale_id: sale.id,
        }, { withCredentials: true });

        window.location.href = checkout.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create sale');
    } finally {
      setLoading(false);
    }
  };

  const cost = calculateCost();

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-8" data-testid="create-sale-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-black text-[#004E89] mb-2" style={{ fontFamily: 'Outfit' }}>
          Post a Sale
        </h1>
        <p className="text-[#5C7A99] mb-8">List your estate or garage sale for the community to find</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Storefront size={24} weight="duotone" />
              Sale Details
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Title *</label>
                <input value={form.title} onChange={(e) => updateField('title', e.target.value)} className="input-neo" placeholder="e.g., Big Estate Sale - Vintage Furniture!" required data-testid="sale-title-input" />
              </div>

              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Category *</label>
                <Select value={form.category} onValueChange={(v) => updateField('category', v)}>
                  <SelectTrigger className="border-2 border-[#004E89] rounded-xl shadow-[2px_2px_0px_#004E89] h-11 font-bold" data-testid="sale-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-[#004E89] rounded-xl shadow-[4px_4px_0px_#004E89]">
                    <SelectItem value="estate">Estate Sale</SelectItem>
                    <SelectItem value="garage">Garage Sale</SelectItem>
                    <SelectItem value="auction">Auction</SelectItem>
                    <SelectItem value="moving">Moving Sale</SelectItem>
                    <SelectItem value="business_closing">Business Going Out</SelectItem>
                    <SelectItem value="craft_fair">Craft Fair</SelectItem>
                    <SelectItem value="food_vendor">Food Vendor</SelectItem>
                    <SelectItem value="carnival">Carnival</SelectItem>
                    <SelectItem value="vendor_event">Vendor Event</SelectItem>
                  </SelectContent>
                </Select>

                {isVendorEvent && (
                  <div className="mt-3 bg-gradient-to-r from-[#7209B7] to-[#00B4D8] rounded-xl p-3 border-2 border-[#004E89]" data-testid="vendor-event-info">
                    <p className="text-xs font-bold text-white">Vendor Event — $25 with unlimited photos. Videos $2 each.</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Description *</label>
                <textarea value={form.description} onChange={(e) => updateField('description', e.target.value)} className="input-neo min-h-[100px] resize-y" placeholder="Describe what items are available..." required data-testid="sale-description-input" />
              </div>

              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-2 uppercase tracking-wide">Item Categories</label>
                <div className="flex flex-wrap gap-2">
                  {ITEM_CATEGORIES.map(cat => (
                    <button key={cat} type="button" onClick={() => toggleItemCategory(cat)}
                      className={`tag cursor-pointer transition-all ${form.items_categories.includes(cat) ? 'bg-[#FF6B35] text-white border-[#004E89]' : 'bg-[#F9F9F6] text-[#5C7A99] border-[#5C7A99]'}`}
                      data-testid={`item-category-${cat.toLowerCase()}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <MapPin size={24} weight="duotone" />
              Location
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Address *</label>
                <input value={form.address} onChange={(e) => updateField('address', e.target.value)} className="input-neo" placeholder="123 Main St" required data-testid="sale-address-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">City *</label>
                  <input value={form.city} onChange={(e) => updateField('city', e.target.value)} className="input-neo" placeholder="City" required data-testid="sale-city-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">State *</label>
                  <input value={form.state} onChange={(e) => updateField('state', e.target.value)} className="input-neo" placeholder="State" required data-testid="sale-state-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Zip *</label>
                  <input value={form.zip_code} onChange={(e) => updateField('zip_code', e.target.value)} className="input-neo" placeholder="12345" required data-testid="sale-zip-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Calendar size={24} weight="duotone" />
              Sale Dates
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Start Date *</label>
                <input type="date" value={form.start_date} onChange={(e) => updateField('start_date', e.target.value)} className="input-neo" required data-testid="sale-start-date-input" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">End Date *</label>
                <input type="date" value={form.end_date} onChange={(e) => updateField('end_date', e.target.value)} className="input-neo" required data-testid="sale-end-date-input" />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Camera size={24} weight="duotone" />
              Photos
              {isVendorEvent ? (
                <span className="text-xs font-normal text-[#4CB944]">(unlimited with vendor listing)</span>
              ) : !isPremium ? (
                <span className="text-xs font-normal text-[#5C7A99]">(5 free, $2 per extra 5)</span>
              ) : null}
            </h2>
            <div className="photo-upload-zone mb-4" onClick={() => fileInputRef.current?.click()} data-testid="photo-upload-zone">
              <Camera size={36} weight="duotone" className="mx-auto text-[#5C7A99] mb-2" />
              <p className="text-sm font-medium text-[#5C7A99]">Click to add photos</p>
              <p className="text-xs text-[#5C7A99]">JPG, PNG, WebP up to 10MB each</p>
            </div>
            <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handlePhotoSelect} className="hidden" data-testid="photo-file-input" />
            {photos.length > 0 && (
              <div className="grid grid-cols-4 gap-3" data-testid="photo-preview-grid">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative rounded-xl overflow-hidden border-2 border-[#004E89] h-24">
                    <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(idx)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center" data-testid={`remove-photo-${idx}`}>
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Videos (available for all, but vendor events get special pricing) */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <VideoCamera size={24} weight="duotone" />
              Videos
              <span className="text-xs font-normal text-[#5C7A99]">($2.00 each)</span>
            </h2>
            <div className="photo-upload-zone mb-4 border-dashed border-[#7209B7]" onClick={() => document.getElementById('video-input')?.click()} data-testid="video-upload-zone">
              <VideoCamera size={36} weight="duotone" className="mx-auto text-[#7209B7] mb-2" />
              <p className="text-sm font-medium text-[#5C7A99]">Click to add videos</p>
              <p className="text-xs text-[#5C7A99]">MP4, MOV, WebM up to 50MB each</p>
            </div>
            <input id="video-input" type="file" multiple accept="video/*" onChange={handleVideoSelect} className="hidden" data-testid="video-file-input" />
            {videos.length > 0 && (
              <div className="space-y-2" data-testid="video-preview-list">
                {videos.map((vid, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-[#F9F9F6] border-2 border-[#004E89] rounded-xl px-4 py-2">
                    <div className="flex items-center gap-2">
                      <VideoCamera size={18} weight="fill" className="text-[#7209B7]" />
                      <span className="text-sm font-medium text-[#004E89] truncate max-w-[200px]">{vid.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-[#FF6B35]">$2.00</span>
                      <button type="button" onClick={() => removeVideo(idx)} className="text-red-500 hover:text-red-700" data-testid={`remove-video-${idx}`}>
                        <X size={16} weight="bold" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Phone size={24} weight="duotone" />
              Contact Info
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Name</label>
                <input value={form.contact_name} onChange={(e) => updateField('contact_name', e.target.value)} className="input-neo" placeholder="Contact name" data-testid="sale-contact-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Phone</label>
                  <input value={form.contact_phone} onChange={(e) => updateField('contact_phone', e.target.value)} className="input-neo" placeholder="(555) 123-4567" data-testid="sale-contact-phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Email</label>
                  <input value={form.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} className="input-neo" placeholder="email@example.com" data-testid="sale-contact-email-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Cost Summary */}
          {!isPremium && user?.role !== 'admin' && (
            hasFreeCredit ? (
              <div className="bg-[#4CB944] bg-opacity-10 border-2 border-[#4CB944] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]" data-testid="free-listing-banner">
                <div className="flex items-center gap-3">
                  <Gift size={28} weight="duotone" className="text-[#4CB944]" />
                  <div>
                    <h3 className="font-bold text-[#004E89]" style={{ fontFamily: 'Outfit' }}>Free Listing Available!</h3>
                    <p className="text-sm text-[#5C7A99]">You have <span className="font-bold text-[#4CB944]">{freeListings}</span> free listing{freeListings !== 1 ? 's' : ''} — this one is on us.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-[#FFD166] border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]" data-testid="cost-summary">
                <h3 className="font-bold text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>Cost Summary</h3>
                <div className="space-y-2 text-sm">
                  {isVendorEvent ? (
                    <>
                      <div className="flex justify-between"><span>Vendor event listing (unlimited photos)</span><span className="font-bold">$25.00</span></div>
                      {videos.length > 0 && (
                        <div className="flex justify-between"><span>Videos ({videos.length} x $2.00)</span><span className="font-bold">${cost.videos.toFixed(2)}</span></div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between"><span>Listing fee (5 photos included)</span><span className="font-bold">${cost.listing.toFixed(2)}</span></div>
                      {cost.photos > 0 && (
                        <div className="flex justify-between"><span>Extra photos ({photos.length - maxFreePhotos} photos)</span><span className="font-bold">${cost.photos.toFixed(2)}</span></div>
                      )}
                      {videos.length > 0 && (
                        <div className="flex justify-between"><span>Videos ({videos.length} x $2.00)</span><span className="font-bold">${(videos.length * 2).toFixed(2)}</span></div>
                      )}
                    </>
                  )}
                  <hr className="border-[#004E89]" />
                  <div className="flex justify-between text-base font-bold"><span>Total</span><span>${(cost.total + (isVendorEvent ? 0 : videos.length * 2)).toFixed(2)}</span></div>
                </div>
                <p className="text-xs text-[#004E89] mt-3 opacity-75">
                  Want unlimited listings? <a href="/pricing" className="underline font-bold">Go Pro for $50/mo</a>
                  {' '} | Refer a friend for a <a href="/referral" className="underline font-bold">free listing</a>
                </p>
              </div>
            )
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-lg" data-testid="submit-sale-button">
            {loading ? 'Creating Sale...' : isPremium || user?.role === 'admin' ? 'Post Sale' : hasFreeCredit ? 'Post Sale (Free Credit)' : `Post Sale - $${cost.total.toFixed(2)}`}
          </button>
        </form>
      </div>
    </div>
  );
}
